import React, { useCallback, useState, memo } from 'react';
import {
  Upload,
  ArrowUpRight,
  Loader2,
  Trash2,
  Grid3x3,
  Sparkles,
  Lightbulb,
  ChevronDown,
  Layers,
  MonitorUp,
  Eraser,
  Crop,
  Frame,
  Grid2x2,
  Download,
} from 'lucide-react';
import { NodeType, NodeStatus } from '@/types';
import { toast } from 'sonner';
import { DropdownMenu, DropdownItem, GridSplitDropdown } from './DropdownMenu';
import { ButtonItem } from './TopFunctionBarButtons';
import { getImageButtons, getVideoButtons, getCommonButtons } from './TopFunctionBarButtons';
import { useFileUpload } from './useFileUpload';
import { ExportActions } from './toolbar/ExportActions';
import { TransformActions } from './toolbar/TransformActions';

/** 工具下拉「高清」项共享文案：六项 featureId 恒为 'upscale' 仅 label 不同，GenerateNode 按 featureLabel 判别 HD 入口（D8），文案与判别同源 */
export const HD_FEATURE_LABEL = '高清';

interface TopFunctionBarProps {
  nodeId: string;
  nodeType: NodeType;
  status: NodeStatus;
  hasMedia: boolean;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'audio';
  images?: string[];
  hasInputAssets?: boolean;
  onUpdate: (id: string, data: any) => void;
  onExtractImage?: (nodeId: string, image: string) => void;
  onDelete?: (id: string) => void;
  onOpenAnnotate?: (nodeId: string) => void;
  onFeatureSelect?: (featureId: string, featureLabel: string) => void;
  onEnterPanorama?: (nodeId: string, imageUrl: string) => void;
  onEnterMultiAngle?: (nodeId: string, imageUrl: string) => void;
  onEnterLighting?: (nodeId: string, imageUrl: string) => void;
}

const TopFunctionBarInner: React.FC<TopFunctionBarProps> = ({
  nodeId,
  nodeType,
  status,
  hasMedia,
  mediaUrl,
  mediaType,
  images,
  hasInputAssets = false,
  onUpdate,
  onExtractImage,
  onDelete,
  onOpenAnnotate,
  onFeatureSelect,
  onEnterPanorama,
  onEnterMultiAngle,
  onEnterLighting,
}) => {
  const isWorking = status === NodeStatus.WORKING;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const { fileInputRef, handleUploadClick, handleFileUpload, acceptTypes } = useFileUpload({
    nodeId, nodeType, onUpdate,
  });

  const handlePlaceholderClick = useCallback((featureName: string) => {
    toast.info(`${featureName} 功能开发中，敬请期待`, { duration: 2000 });
  }, []);

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const url = mediaUrl || '';
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `bong-ai-${Date.now()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [mediaUrl]);

  const handleGridDropdown = useCallback((item: string) => {
    if (onFeatureSelect) {
      onFeatureSelect('grid', item);
    } else {
      toast.info(`九宫格: ${item} 功能开发中`, { duration: 2000 });
    }
    setOpenDropdown(null);
  }, [onFeatureSelect]);

  const handleUpscaleDropdown = useCallback((item: string) => {
    if (onFeatureSelect) {
      onFeatureSelect('upscale', item);
    } else {
      toast.info(`高清: ${item} 功能开发中`, { duration: 2000 });
    }
    setOpenDropdown(null);
  }, [onFeatureSelect]);

  const handleGridSplit = useCallback((rows: number, cols: number) => {
    // P0(v9): label 格式改为 cols×rows（横向×纵向），符合用户直觉
    // 例如 1行3列 → "3×1宫格切分"（横3纵1）
    const label = `${cols}×${rows}宫格切分`;
    if (onFeatureSelect) {
      onFeatureSelect('split', label);
    } else {
      toast.info(`宫格切分: ${cols}×${rows} 功能开发中`, { duration: 2000 });
    }
    setOpenDropdown(null);
  }, [onFeatureSelect]);

  const handleAnnotate = useCallback(() => {
    if (onOpenAnnotate) {
      onOpenAnnotate(nodeId);
    } else {
      toast.info('标注面板开发中，敬请期待', { duration: 2000 });
    }
  }, [nodeId, onOpenAnnotate]);

  const handlePanoramaClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEnterPanorama && mediaUrl && mediaType === 'image') {
      onEnterPanorama(nodeId, mediaUrl);
    } else {
      toast.info('请先上传图片素材');
    }
  }, [nodeId, mediaUrl, mediaType, onEnterPanorama]);

  const handleMultiAngleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEnterMultiAngle && mediaUrl && mediaType === 'image') {
      onEnterMultiAngle(nodeId, mediaUrl);
    } else {
      toast.info('请先上传图片素材');
    }
  }, [nodeId, mediaUrl, mediaType, onEnterMultiAngle]);

  const handleLightingClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEnterLighting && mediaUrl && mediaType === 'image') {
      onEnterLighting(nodeId, mediaUrl);
    } else {
      toast.info('请先上传图片素材');
    }
  }, [nodeId, mediaUrl, mediaType, onEnterLighting]);

  const isImageNode = nodeType === NodeType.IMAGE_GENERATOR || nodeType === NodeType.IMAGE_EDITOR || nodeType === NodeType.PROMPT_INPUT || (nodeType === NodeType.MEDIA_UPLOAD && mediaType === 'image');
  const isVideoNode = nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.VIDEO_COMPOSE || (nodeType === NodeType.MEDIA_UPLOAD && mediaType === 'video');

  const buttons = isImageNode
    ? getImageButtons({ onPanorama: handlePanoramaClick, onMultiAngle: handleMultiAngleClick, onLighting: handleLightingClick, onDownload: handleDownload, mediaUrl, mediaType })
    : isVideoNode
      ? getVideoButtons({ onDownload: handleDownload, mediaUrl, mediaType })
      : getCommonButtons({ onDownload: handleDownload, mediaUrl, mediaType });

  const renderButton = (btn: ButtonItem) => {
    if (!btn.show) return null;

    const Icon = btn.icon;
    const isPlaceholder = btn.isPlaceholder;
    const isDropdownOpen = openDropdown === btn.id;

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (btn.hasDropdown) {
        setOpenDropdown(isDropdownOpen ? null : btn.id);
      } else if (btn.id === 'annotate') {
        handleAnnotate();
      } else if (isPlaceholder) {
        handlePlaceholderClick(btn.title || btn.label || btn.id);
      } else {
        btn.onClick?.(e);
      }
    };

    return (
      <div key={btn.id} className="relative">
        <button
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          title={btn.title}
          className={`
            flex items-center gap-[3px]
            h-9 px-2.5
            rounded-md
            border-0
            transition-all
            ${isPlaceholder && btn.id !== 'annotate'
              ? 'bg-transparent text-white cursor-default hover:bg-white/10'
              : 'bg-transparent text-white hover:text-white hover:bg-white/10 cursor-pointer'
            }
          `}
        >
          <Icon size={16} />
          {btn.label && (
            <span className="text-sm font-medium whitespace-nowrap">{btn.label}</span>
          )}
          {btn.hasDropdown && (
            <ChevronDown size={14} className={`text-white/60 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          )}
        </button>

        {btn.id === 'grid' && (
          <DropdownMenu visible={isDropdownOpen} onClose={() => setOpenDropdown(null)}>
            <DropdownItem icon={Grid3x3} label="多机位九宫格" onClick={() => handleGridDropdown('多机位九宫格')} />
            <DropdownItem icon={Layers} label="剧情推演四宫格" onClick={() => handleGridDropdown('剧情推演四宫格')} />
            <DropdownItem icon={Grid2x2} label="25宫格连贯分镜" onClick={() => handleGridDropdown('25宫格连贯分镜')} />
            <DropdownItem icon={Lightbulb} label="电影级光影校正" onClick={() => handleGridDropdown('电影级光影校正')} />
            <DropdownItem icon={Frame} label="角色三视图生成" onClick={() => handleGridDropdown('角色三视图生成')} />
            <DropdownItem icon={MonitorUp} label="画面推演 - 3秒后" onClick={() => handleGridDropdown('画面推演 - 3秒后')} />
            <DropdownItem icon={MonitorUp} label="画面推演 - 5秒前" onClick={() => handleGridDropdown('画面推演 - 5秒前')} />
          </DropdownMenu>
        )}

        {btn.id === 'upscale' && (
          <DropdownMenu visible={isDropdownOpen} onClose={() => setOpenDropdown(null)}>
            <DropdownItem icon={Sparkles} label={HD_FEATURE_LABEL} onClick={() => handleUpscaleDropdown(HD_FEATURE_LABEL)} />
            <DropdownItem icon={MonitorUp} label="扩图" onClick={() => handleUpscaleDropdown('扩图')} />
            <DropdownItem icon={Frame} label="重绘" onClick={() => handleUpscaleDropdown('重绘')} />
            <DropdownItem icon={Eraser} label="擦除" onClick={() => handleUpscaleDropdown('擦除')} />
            <DropdownItem icon={Crop} label="抠图" onClick={() => handleUpscaleDropdown('抠图')} />
            <DropdownItem icon={Crop} label="裁剪" onClick={() => handleUpscaleDropdown('裁剪')} />
          </DropdownMenu>
        )}

        {btn.id === 'split' && (
          <GridSplitDropdown
            visible={isDropdownOpen}
            onSelect={handleGridSplit}
            onClose={() => setOpenDropdown(null)}
          />
        )}
      </div>
    );
  };

  if (!hasMedia) {
    if (hasInputAssets) return null;
    return (
      <div className="flex items-center gap-2 nodrag bg-[#262626]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg">
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          className="hidden"
          onChange={handleFileUpload}
        />
        <button
          onClick={handleUploadClick}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all cursor-pointer"
        >
          <Upload size={16} />
          <span className="text-sm font-medium">上传</span>
        </button>
        {isWorking && (
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#262626]/90 border border-white/10">
            <Loader2 className="animate-spin w-[18px] h-[18px] text-cyan-400" />
          </div>
        )}
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(nodeId); }}
            onMouseDown={(e) => e.stopPropagation()}
            title="删除节点"
            className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-red-400 hover:bg-white/10 transition-all cursor-pointer"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 nodrag bg-[#262626]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        className="hidden"
        onChange={handleFileUpload}
      />

      {!hasInputAssets && (
        <button
          onClick={handleUploadClick}
          onMouseDown={(e) => e.stopPropagation()}
          title="上传替换"
          className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all cursor-pointer"
        >
          <Upload size={16} />
        </button>
      )}

      {buttons.map((btn) => renderButton(btn))}

      <ExportActions
        nodeId={nodeId}
        nodeType={nodeType}
        status={status}
        hasMedia={hasMedia}
        mediaUrl={mediaUrl}
        mediaType={mediaType}
      />

      <TransformActions
        nodeId={nodeId}
        nodeType={nodeType}
        status={status}
        hasMedia={hasMedia}
        mediaType={mediaType}
        images={images}
        onGridSplit={onFeatureSelect ? (id) => onFeatureSelect('split', '宫格切分') : undefined}
        onPanorama={onEnterPanorama ? (id) => { if (mediaUrl) onEnterPanorama(id, mediaUrl); } : undefined}
      />

      {mediaType === 'image' && images && images.length > 1 && onExtractImage && (
        <button
          onClick={(e) => { e.stopPropagation(); if (mediaUrl) onExtractImage(nodeId, mediaUrl); }}
          onMouseDown={(e) => e.stopPropagation()}
          title="提取当前图片为新节点"
          className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-cyan-400 hover:bg-white/10 transition-all cursor-pointer"
        >
          <ArrowUpRight size={16} />
        </button>
      )}

      {isWorking && (
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#262626]/90 border border-white/10">
          <Loader2 className="animate-spin w-[18px] h-[18px] text-cyan-400" />
        </div>
      )}

      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(nodeId); }}
          onMouseDown={(e) => e.stopPropagation()}
          title="删除节点"
          className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-red-400 hover:bg-white/10 transition-all cursor-pointer"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
};

export const TopFunctionBar = memo(TopFunctionBarInner, (prevProps, nextProps) => {
  const prevImages = prevProps.images;
  const nextImages = nextProps.images;
  const imagesEqual = prevImages === nextImages || (
    Array.isArray(prevImages) && Array.isArray(nextImages) &&
    prevImages.length === nextImages.length &&
    prevImages.every((img, i) => img === nextImages[i])
  );

  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType &&
    prevProps.status === nextProps.status &&
    prevProps.hasMedia === nextProps.hasMedia &&
    prevProps.mediaUrl === nextProps.mediaUrl &&
    prevProps.mediaType === nextProps.mediaType &&
    prevProps.hasInputAssets === nextProps.hasInputAssets &&
    imagesEqual
  );
});
